import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContextService } from '@core/services/core/context.service';
import { LogService } from '@core/services/core/log.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { TecuidamosService } from '@core/services/core/tecuidamos.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { FileHelperService } from '@core/services/helpers/file-helper.service';
import { GeocoderHelperService } from '@core/services/helpers/geocoder-helper.service';
import { UrlHelper } from '@core/services/helpers/url-helper.service';
import { InAppBrowser, InAppBrowserEvent, InAppBrowserObject } from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { AllFeatureFlags } from '@models/mmobile';
import { ERROR_ICON_PATH } from '@models/non-renewal';
import { TeCuidamosUserType } from '@models/user';
import { spinner } from '@utils/rxjs';
import { stopVideos } from '@utils/utils';
import { defer, of } from 'rxjs';
import { distinctUntilKeyChanged, filter, finalize, switchMap } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class TeCuidamosHelper {

  readonly teCuidamosInAppBrowserOptions = {
    ios: 'hidenavigationbuttons=yes,toolbarposition=top,toolbarcolor=#d81e05,closebuttoncolor=#ffffff,location=no,lefttoright=yes,closebuttoncaption=X,beforeload=yes',
    android: 'hidenavigationbuttons=yes,toolbarposition=top,toolbarcolor=#d81e05,hideurlbar=yes,closebuttoncolor=#ffffff,location=yes,lefttoright=no,closebuttoncaption=X,beforeload=yes'
  };

  cardImageFrontSafeResource: SafeResourceUrl;
  cardImageFrontSrc: string;

  constructor(
    public ctx: ContextService,
    private sanitizer: DomSanitizer,
    private nav: NavigationHelper,
    private teCuidamos: TecuidamosService,
    private ui: UiHelperService,
    private url: UrlHelper,
    private mmobileHelper: MMobileHelperService,
    private iab: InAppBrowser,
    private platform: Platform,
    private log: LogService,
    private fileHelper: FileHelperService,
    private geoHelperService: GeocoderHelperService
  ) {}

  getImage(image: string) {
    return this.sanitizer.bypassSecurityTrustUrl(
      'data:image/jpeg;base64,' + image
    );
  }

  getStyledImage(image: SafeResourceUrl) {
    return this.sanitizer.bypassSecurityTrustStyle(
      'data:image/jpeg;base64,' + `url('${image}')`
    );
  }

  navTo(event: any, memberState?: TeCuidamosUserType) {
    switch (event) {
      case 'saving':
        this.nav.to('tecuidamos/tc-saving');
        break;
      case 'service':
        this.nav.to('tecuidamos/tc-service-list', { memberState });
        break;
      case 'raffle':
        this.nav.to('tecuidamos/tc-raffle-list', { memberState });
        break;
      case 'faq':
        this.nav.to('tecuidamos/tc-faq');
        break;
      case 'consultation':
        this.nav.to('tecuidamos/tc-consultation');
        break;
      case 'signup':
        this.nav.to('tecuidamos/tc-register-form');
        break;
      case 'card':
        this.nav.to('tecuidamos/tc-card');
        break;
      default:
        break;
    }
  }

  async openSaviaTeCuidamosUrl(page: string, redirectTo?: string) {
    const url = redirectTo
      ? `${this.mmobileHelper.getCustomConfigData('TE_CUIDAMOS', page)}?redirect=/${redirectTo}`
      : this.mmobileHelper.getCustomConfigData('TE_CUIDAMOS', page);
    await this.ui.presentLoading('teCuidamos-sso');
    this.teCuidamos.getClubMapfreUrlWithParams(url, page)
      .pipe(
        switchMap(link => {
          return this.openTeCuidamosInAppBrowser(link);
        }),
        finalize(() => this.ui.dismissLoading('teCuidamos-sso'))
      )
      .subscribe(
        () => {},
        () => {
          this.ui.presentOneButtonAlert({
            image: ERROR_ICON_PATH,
            title: 'TECUIDAMOS.TC_HOME.ERROR_TITLE',
            description: 'TECUIDAMOS.TC_HOME.ERROR_MESSAGE',
            cssClass: ['document-error-alert']
          });
          this.ui.dismissLoading('teCuidamos-sso');
      });
  }

  openLoyaliaTeCuidamosUrl() {
    const openUrl = new URL(this.mmobileHelper.getCustomConfigData('TE_CUIDAMOS', 'loyalia'));
    return this.teCuidamos.getLoyaliaSsoToken()
      .pipe(spinner('teCuidamosSsoToken'))
      .subscribe(
        data => {
          openUrl.searchParams.append('auth', data.token);
          this.url.openExternal(openUrl);
        },
        () => { this.url.openExternal(openUrl); }
      );
  }

  openTeCuidamosFromPushNotification(page) {
    return defer(() => this.ui.presentLoading(page)).pipe(
      switchMap(() => this.teCuidamos.getState$()),
      filter(state => Object.keys(state).length !== 0),
      distinctUntilKeyChanged('memberState'),
      switchMap((state) => {
        this.ui.dismissLoading(page);
        const teCuidamosState = state;
        const teCuidamosSavia = this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.TECUIDAMOS_SAVIA);

        switch (teCuidamosState.memberState) {
          case TeCuidamosUserType.SIGNUP:
            if (this.ctx.isApp && teCuidamosSavia) {
              return this.openSaviaTeCuidamosUrl('subscribeTeCuidamos').then(() => true);
            } else {
              return this.nav.toNew('tecuidamos/tc-register-presentation', {
                state: { deeplinkOriginNavigation: true }
              });
            }
          case TeCuidamosUserType.ERROR:
          case TeCuidamosUserType.CANT:
            // tc-cant-register page shows ion-menu-button therefore deeplinkOriginNavigation is not needed
            return this.nav.toNew('tecuidamos/tc-cant-register');

          case TeCuidamosUserType.LOGIN:
            if (teCuidamosSavia) {
              return this.openSaviaTeCuidamosUrl('raffles').then(() => true);
            } else {
              return this.nav.toNew('tecuidamos/tc-raffle-list', {
                state: {
                  memberState: teCuidamosState.memberState,
                  deeplinkOriginNavigation: true
                }
              });
            }
          default:
          this.log.d(this, 'Unmatched teCuidamos memberState', teCuidamosState);
          return of(true);
        }
      })
    );
  }

  private async openTeCuidamosInAppBrowser(url: string | URL) {
    const { href, host } = typeof url === 'string' ? new URL(url) : url;
    this.log.d(this, 'Opening teCuidamos url requested:', href);
    this.log.d(this, 'Host:', host);
    stopVideos();
    if (!this.platform.is('cordova')) {
      window.open(href, '_self');
      return;
    }

    const iabOptions = this.platform.is('ios')
      ? this.teCuidamosInAppBrowserOptions.ios
      : this.teCuidamosInAppBrowserOptions.android;

    await this.platform.ready();

    const iabObject = this.iab.create(href, '_blank', iabOptions);
    this.setTeCuidamosIabCallbacks(iabObject, host);
  }

  private setTeCuidamosIabCallbacks(iabObject: InAppBrowserObject, host: string) {
    iabObject.on('beforeload').subscribe((event: InAppBrowserEvent) => {
      this.log.d(this, 'InAppBrowser beforeload event data:', event);
      if (event.url.includes(host)) {
        iabObject._loadAfterBeforeload(event.url);
        return;
      }
      this.openNewIab(event.url, host);
    }, (error) => {
      this.log.d(this, 'InAppBrowser beforeload error:', error);
    }, () => {
      this.log.d(this, 'InAppBrowser beforeload event complete');
    });

    iabObject.on('message').subscribe((event: InAppBrowserEvent) => {
      this.log.d(this, 'InAppBrowser message event data:', event);
      if (!event.data) {
        this.log.d(this, 'InAppBrowser message event data is empty');
        return;
      }

      switch (event.data.type) {
        case 'closeInAppBrowser':
          iabObject.close();
          break;
        case 'openBase64Pdf':
          this.fileHelper.openBase64(event.data?.base64);
          break;
        case 'openUrl':
          this.openNewIab(event.data?.url, host);
          break;
        case 'internal_navigation':
          iabObject.close();
          this.nav.toNew(event?.data?.url);
          break;
        case 'requestLocationPermission':
          this.geoHelperService.checkLocationUserPermission();
          break;
        default:
          this.log.d(this, 'InAppBrowser message event data type not recognized:', event.data.type);
          break;
      }
    }, (error) => {
      this.log.d(this, 'InAppBrowser message error:', error);
    }, () => {
      this.log.d(this, 'InAppBrowser message event complete');
    });
  }

  private openNewIab(url: string, host: string) {
    this.log.d(this, 'Open external url:', url);
    const newIab = this.iab.create(url, '_system');
    this.setTeCuidamosIabCallbacks(newIab, host);
  }
}
